import { getFontCss, styleConstants, Tag } from '@paywithextend/components'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectBulkUpdateErrorDetailMapFromState,
  selectBulkUpdateTopLevelErrorFromState,
  selectBulkUpdatingFromState,
  selectFilteredCreditCardListModel,
  selectSelectedVirtualCardListModels,
  selectUniqueCreditCardIds,
  selectUpdateableSelectedVirtualCardListModels,
} from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.selectors'
import MultipleCardsIcon from 'src/assets/svgs/MixedCardTypes.svg'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import BetaTag from 'src/components/BetaTag/BetaTag'
import { creditCardOption } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import TestId from 'src/components/TestId/TestId'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { ImageNames, VirtualCardTypes } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import {
  RecurrencePeriod,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import { createSetIsBulkUpdateVirtualCardsFlyoutOpen } from 'src/redux/actions/app.actions'
import { getCreditCard } from 'src/redux/actions/data/creditCards.actions'
import { patchBulkVirtualCardUpdateAndCloseModal } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectNullSafeCurrencyFromState,
  selectNullSafeLocaleFromState,
} from 'src/redux/selectors/auth.selectors'
import { toCents } from 'src/utils/currency.utils'
import { css } from 'styled-components'

const BulkUpdateVirtualCardFlyout: React.FC = (): React.ReactElement => {
  const selectedCards = useSelector(selectSelectedVirtualCardListModels)
  const updateableCards = useSelector(
    selectUpdateableSelectedVirtualCardListModels,
  )
  const uniqueCreditCardIds = useSelector(selectUniqueCreditCardIds)
  const filteredCreditCardListModel = useSelector(
    selectFilteredCreditCardListModel,
  )
  const bulkUpdating = useSelector(selectBulkUpdatingFromState)
  const bulkUpdateTopLevelError = useSelector(
    selectBulkUpdateTopLevelErrorFromState,
  )
  const bulkUpdatelErrorDetailMap = useSelector(
    selectBulkUpdateErrorDetailMapFromState,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)
  const defaultCurrency = useSelector(selectNullSafeCurrencyFromState)

  // using background dispatch here because this is routeless
  const dispatch = useBackgroundDispatch()

  const { analytics } = useAnalyticsContext()

  const standardCount = updateableCards.filter(
    (vc) => !vc.recurs && vc.direct !== true,
  ).length
  const recurringCount = updateableCards.filter(
    (vc) => vc.recurs && vc.direct !== true,
  ).length
  const standardGuestCount = updateableCards.filter(
    (vc) => !vc.recurs && vc.direct === true,
  ).length
  const recurringGuestCount = updateableCards.filter(
    (vc) => vc.recurs && vc.direct === true,
  ).length

  const recurrenceEditable =
    standardGuestCount === 0 &&
    standardCount === 0 &&
    (recurringGuestCount > 0 || recurringCount > 0)
  const mixedCards =
    (recurringGuestCount > 0 || recurringCount > 0) &&
    (standardGuestCount > 0 || standardCount > 0)
  const canUpdateAll = selectedCards.length === updateableCards.length

  const commonBulkUpdateValue =
    updateableCards &&
    updateableCards.every(
      (card) => card?.balance === updateableCards[0]?.balance,
    )
      ? updateableCards[0]?.balance
      : undefined
  const commonBulkUpdateCurrency =
    updateableCards &&
    updateableCards.every(
      (card) => card?.currency === updateableCards[0]?.currency,
    )
      ? updateableCards[0]?.currency
      : defaultCurrency // TODO: properly handle multiple currencies
  const commonActiveUntilValue =
    updateableCards &&
    updateableCards.every(
      (card) =>
        card?.validToTimezonedString ===
        updateableCards[0]?.validToTimezonedString,
    )
      ? updateableCards[0]?.validToTimezonedString
      : undefined
  const commonNotesValue =
    updateableCards &&
    updateableCards.every((card) => card?.notes === updateableCards[0]?.notes)
      ? updateableCards[0]?.notes
      : undefined

  const initialRecurrenceFields = useMemo(
    (): Partial<RecurrenceFormValues> =>
      updateableCards && {
        recurrenceDollars: updateableCards.every(
          (card) =>
            card?.recurrence?.balance ===
            updateableCards[0]?.recurrence?.balance,
        )
          ? updateableCards[0]?.recurrence?.balance
          : undefined,
        period: updateableCards.every(
          (card) =>
            card?.recurrence?.period === updateableCards[0]?.recurrence?.period,
        )
          ? updateableCards[0]?.recurrence?.period
          : undefined,
        interval: updateableCards.every(
          (card) =>
            card?.recurrence?.interval ===
            updateableCards[0]?.recurrence?.interval,
        )
          ? updateableCards[0]?.recurrence?.interval
          : undefined,
        terminator: updateableCards.every(
          (card) =>
            card?.recurrence?.terminator ===
            updateableCards[0]?.recurrence?.terminator,
        )
          ? updateableCards[0]?.recurrence?.terminator
          : undefined,
        count: updateableCards.every(
          (card) =>
            card?.recurrence?.remainingCount ===
            updateableCards[0]?.recurrence?.remainingCount,
        )
          ? updateableCards[0]?.recurrence?.remainingCount
          : undefined,
        untilMoment: updateableCards.every(
          (card) =>
            card?.recurrence?.untilTimezonedMoment(card?.timezone) ===
            updateableCards[0]?.recurrence?.untilTimezonedMoment(
              updateableCards[0]?.timezone,
            ),
        )
          ? updateableCards[0]?.recurrence?.untilTimezonedMoment(
              updateableCards[0]?.timezone,
            )
          : undefined,
        byWeekDay: updateableCards.every(
          (card) =>
            card?.recurrence?.byWeekDay ===
            updateableCards[0]?.recurrence?.byWeekDay,
        )
          ? updateableCards[0]?.recurrence?.byWeekDay
          : undefined,
        byMonthDay: updateableCards.every(
          (card) =>
            card?.recurrence?.byMonthDay ===
            updateableCards[0]?.recurrence?.byMonthDay,
        )
          ? updateableCards[0]?.recurrence?.byMonthDay
          : undefined,
        byYearDay: updateableCards.every(
          (card) =>
            card?.recurrence?.byYearDay ===
            updateableCards[0]?.recurrence?.byYearDay,
        )
          ? updateableCards[0]?.recurrence?.byYearDay
          : undefined,
      },
    [updateableCards],
  )

  const multipleCards = uniqueCreditCardIds.length > 1

  const cornerImage = (): string => {
    const standardImage = useColorOrGrayscaleImage(ImageNames.CardTypeStandard)
    const recurringImage = useColorOrGrayscaleImage(
      ImageNames.CardTypeRecurring,
    )
    const guestImage = useColorOrGrayscaleImage(ImageNames.CardTypeGuest)
    const recurringGuestImage = useColorOrGrayscaleImage(
      ImageNames.CardTypeRecurringGuest,
    )

    const standardCardsOnly =
      standardCount > 0 &&
      standardGuestCount === 0 &&
      recurringGuestCount === 0 &&
      recurringCount === 0
    const recurringCardsOnly =
      standardCount === 0 &&
      standardGuestCount === 0 &&
      recurringGuestCount === 0 &&
      recurringCount > 0
    const guestCardsOnly =
      standardCount === 0 &&
      standardGuestCount > 0 &&
      recurringGuestCount === 0 &&
      recurringCount === 0
    const recurringGuestCardsOnly =
      standardCount === 0 &&
      standardGuestCount === 0 &&
      recurringGuestCount > 0 &&
      recurringCount === 0

    if (standardCardsOnly) {
      return standardImage
    } else if (recurringCardsOnly) {
      return recurringImage
    } else if (guestCardsOnly) {
      return guestImage
    } else if (recurringGuestCardsOnly) {
      return recurringGuestImage
    } else {
      return MultipleCardsIcon
    }
  }

  useEffect(() => {
    if (uniqueCreditCardIds[0] && uniqueCreditCardIds.length === 1) {
      dispatch(getCreditCard(uniqueCreditCardIds[0]))
    }
  }, [])

  const selectedVirtualCardsTags = (): React.ReactElement => {
    const cardTypeArray = []

    if (standardCount > 0) {
      cardTypeArray.push(
        <Tag
          text={
            <Trns
              source={'Standard Card (${count})'}
              expressions={{
                count: standardCount.toString(),
              }}
            />
          }
        />,
      )
    }
    if (recurringCount > 0) {
      cardTypeArray.push(
        <Tag
          text={
            <Trns
              source={'Recurring Card (${count})'}
              expressions={{
                count: recurringCount.toString(),
              }}
            />
          }
        />,
      )
    }
    if (standardGuestCount > 0) {
      cardTypeArray.push(
        <Tag
          text={
            <Trns
              source={'Guest Card (${count})'}
              expressions={{
                count: standardGuestCount.toString(),
              }}
            />
          }
        />,
      )
    }
    if (recurringGuestCount > 0) {
      cardTypeArray.push(
        <Tag
          text={
            <Trns
              source={'Recurring Guest Card (${count})'}
              expressions={{
                count: recurringGuestCount.toString(),
              }}
            />
          }
        />,
      )
    }

    return (
      <div
        css={css`
          margin: 10px 0px;
          display: flex;
          gap: 10px;
        `}
      >
        {cardTypeArray.map((item) => item)}
      </div>
    )
  }

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
    recurrenceFields: RecurrenceFormValues,
  ): void => {
    const recurrenceFieldsUpdated = !!Object.values(recurrenceFields).find(
      (item) => item !== undefined,
    )
    dispatch(
      patchBulkVirtualCardUpdateAndCloseModal({
        virtualCardIds: updateableCards.map((vc) => vc.id),
        notes: flatFields.notes,
        balanceCents:
          flatFields.balanceDollars !== undefined
            ? toCents(flatFields.balanceDollars)
            : undefined,
        validTo:
          !mixedCards &&
          !recurrenceEditable &&
          flatFields.validToMoment !== undefined
            ? flatFields.validToMoment.format('YYYY-MM-DD')
            : undefined,
        recurs:
          recurrenceEditable && recurrenceFieldsUpdated
            ? !!recurrenceFields
            : undefined,
        recurrence:
          recurrenceEditable && recurrenceFieldsUpdated
            ? {
                balanceCents:
                  recurrenceFields.recurrenceDollars !== undefined
                    ? toCents(recurrenceFields.recurrenceDollars)
                    : undefined,
                period: recurrenceFields.period,
                interval: recurrenceFields.interval,
                terminator: recurrenceFields.terminator,
                count: recurrenceFields.count,
                until:
                  recurrenceFields.terminator === RecurrenceTerminator.Date
                    ? recurrenceFields.untilMoment?.format('YYYY-MM-DD')
                    : undefined,
                byWeekDay:
                  recurrenceFields.period === RecurrencePeriod.Weekly
                    ? recurrenceFields.byWeekDay
                    : undefined,
                byMonthDay:
                  recurrenceFields.period === RecurrencePeriod.Monthly
                    ? recurrenceFields.byMonthDay
                    : undefined,
                byYearDay:
                  recurrenceFields.period === RecurrencePeriod.Yearly
                    ? recurrenceFields.byYearDay
                    : undefined,
                resetCount:
                  recurrenceFields.terminator !== undefined
                    ? recurrenceFields.terminator === RecurrenceTerminator.Count
                    : undefined,
              }
            : undefined,
      }),
    )

    analytics?.track('update vcn', {
      interaction: 'button press',
      intent: 'update vcn',
      bulk: true,
      number_of_cards: updateableCards.length,
    })
  }

  return (
    <RightFlyout
      width={RightFlyoutBodyWidths.Action}
      onClose={(): void => {
        dispatch(createSetIsBulkUpdateVirtualCardsFlyoutOpen(false))
      }}
    >
      <ActionRightFlyoutContent
        css={css`
          overflow: visible;
        `}
        title={
          <div
            css={css`
              display: flex;
              gap: 10px;
              align-items: center;
            `}
          >
            <Trns source={'Bulk Update Virtual Cards'} />
            <BetaTag
              css={css`
                z-index: 100;
              `}
              openLeft
            />
          </div>
        }
        specificElements={{
          imageSrc: cornerImage(),
        }}
        pickerSubtitle={selectedVirtualCardsTags()}
        onClose={(): void => {
          dispatch(createSetIsBulkUpdateVirtualCardsFlyoutOpen(false))
        }}
      >
        <TestId testId={'bulkUpdateVirtualCardForm'}>
          <VirtualCardForm
            id={'bulkUpdateVirtualCard'}
            topText={
              !canUpdateAll || mixedCards ? (
                <div
                  css={css`
                    margin: 10px 0px 20px 0px;
                  `}
                >
                  {!canUpdateAll && (
                    <SoftAlertBox
                      css={css`
                        padding: 16px 20px;
                      `}
                    >
                      <Trns
                        source={
                          'You can only edit virtual cards you have created or have access to as a budget user'
                        }
                      />
                    </SoftAlertBox>
                  )}
                  {mixedCards && (
                    <SoftAlertBox
                      css={css`
                        margin-top: ${!canUpdateAll ? css`24px` : css`0px`};
                        padding: 16px 20px;
                      `}
                    >
                      <span>
                        <Trns
                          source={
                            '${bolded} will not let you make changes to the available credit and active until date across virtual cards'
                          }
                          expressions={{
                            bolded: (
                              <span
                                css={css`
                                  ${getFontCss(
                                    12,
                                    styleConstants.colors.gray_darkest,
                                    styleConstants.fontFamilies.medium,
                                  )};
                                `}
                              >
                                <Trns source={'Updating multiple card types'} />
                              </span>
                            ),
                          }}
                        />
                      </span>
                    </SoftAlertBox>
                  )}
                </div>
              ) : undefined
            }
            updateProps={{
              baseFlatFields: {
                balanceDollars: commonBulkUpdateValue,
                currency: commonBulkUpdateCurrency,
                validToMoment:
                  commonActiveUntilValue !== undefined
                    ? moment(commonActiveUntilValue)
                    : undefined,
              },
              baseRecurrenceFields: initialRecurrenceFields,
              timezone: moment.tz.guess(),
            }}
            buttonText={
              updateableCards && updateableCards.length > 1 ? (
                <Trns source={'Update Virtual Cards'} />
              ) : (
                <Trns source={'Update Virtual Card'} />
              )
            }
            creditCardProps={{
              labelCreditCardsAsAccounts: filteredCreditCardListModel
                ? !filteredCreditCardListModel?.isBudget
                : true,
              isFormDisabled: false,
              creditCardOptions: filteredCreditCardListModel
                ? [creditCardOption(filteredCreditCardListModel)]
                : [],
              accountMessage: multipleCards ? (
                <Trns
                  source={"You've selected cards from multiple funding sources"}
                />
              ) : undefined,
              multipleCards: multipleCards,
            }}
            onSuccessfulSubmit={onSuccessfulSubmit}
            loading={bulkUpdating}
            userFieldLabel={<Trns source={'Update Virtual Cards'} />} // placeholder
            notesEditable
            recipientNamesEditable={false}
            amountProps={{
              amountEditable: !mixedCards,
              amountEditableOptional: true,
              amountPlaceholder: translateSingleString(
                locale,
                'Leave blank if no change is desired',
              ),
              amountValue: commonBulkUpdateValue,
            }}
            recurrenceEditable={recurrenceEditable}
            topLevelError={bulkUpdateTopLevelError}
            errorDetailMap={bulkUpdatelErrorDetailMap}
            typeofCard={VirtualCardTypes.bulk}
            initialFlatFields={{
              balanceDollars: commonBulkUpdateValue,
              currency: commonBulkUpdateCurrency,
              notes: commonNotesValue,
              validToMoment:
                commonActiveUntilValue !== undefined
                  ? moment(commonActiveUntilValue)
                  : undefined,
            }}
            initialRecurrenceFields={
              recurrenceEditable ? initialRecurrenceFields : undefined
            }
            hideWasOnUpdate
            explicitSelectNone
          />
        </TestId>
      </ActionRightFlyoutContent>
    </RightFlyout>
  )
}

export default BulkUpdateVirtualCardFlyout
